import { useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SelectOption } from '@kula-ai/components';
import {
  fetchUserSearch,
  searchedUserName,
  type SearchedUser,
} from './fetchUserSearch';

export const toCcBccOption = (user: SearchedUser): SelectOption => {
  const label = searchedUserName(user);
  return {
    value: String(user.id),
    label,
    // Only when the label is a name — otherwise it already IS the email.
    description: label === user.email ? undefined : (user.email ?? undefined),
    image: user.avatar_thumb_url ?? undefined,
  };
};

/** Saved `cc_participants` / `bcc_participants` rows → options (ENG-3766). */
export const participantsToOptions = (
  participants: unknown
): SelectOption[] => {
  if (!Array.isArray(participants)) return [];
  return participants
    .filter(
      (p): p is Record<string, unknown> =>
        typeof p === 'object' &&
        p !== null &&
        p.id !== undefined &&
        p.id !== null
    )
    .map((p) => {
      // The participant partial emits full_name / first_name / last_name and
      // avatar_url — not `name` / `avatar`.
      const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
      const name =
        str(p.full_name) ||
        `${str(p.first_name)} ${str(p.last_name)}`.trim() ||
        str(p.name);
      const email = typeof p.email === 'string' ? p.email : undefined;
      return {
        value: String(p.id),
        label: name || email || `User ${p.id}`,
        description: name ? email : undefined,
        image: str(p.avatar_url) || str(p.avatar) || undefined,
      };
    });
};

/** Concatenates option lists, keeping the first entry per value. */
export const mergeOptionsByValue = (
  ...lists: SelectOption[][]
): SelectOption[] => {
  const byValue = new Map<string, SelectOption>();
  lists.flat().forEach((option) => {
    const key = String(option.value);
    if (!byValue.has(key)) byValue.set(key, option);
  });
  return [...byValue.values()];
};

export const ccBccUserSearchQueryKey = (query: string) =>
  ['account-users-search', query] as const;

/**
 * CC/BCC options: every active user, searched server-side.
 *
 * Being copied needs no send permission, so this is everyone — not the
 * delegatable senders these surfaces used to reuse (ENG-3766).
 */
export function useCcBccUserOptions(knownOptions: SelectOption[] = []) {
  const queryClient = useQueryClient();
  // Everyone the pickers have seen. The menu only holds the last search, so
  // callers mapping a selected id back to an email need this.
  const seenRef = useRef(new Map<string, SelectOption>());

  const register = useCallback((options: SelectOption[]) => {
    options.forEach((option) => {
      seenRef.current.set(String(option.value), option);
    });
    return options;
  }, []);

  const { data: firstPage = [] } = useQuery({
    queryKey: ccBccUserSearchQueryKey(''),
    queryFn: ({ signal }) => fetchUserSearch({ signal }),
    staleTime: 60_000,
    select: (users) => users.map(toCcBccOption),
  });

  // Saved recipients first: a chip outside the first page has no other way to
  // resolve its label on a fresh load.
  const initialOptions = useMemo(
    () => register(mergeOptionsByValue(knownOptions, firstPage)),
    [knownOptions, firstPage, register]
  );

  const loadOptions = useCallback(
    async (query: string): Promise<SelectOption[]> => {
      const term = query?.trim() ?? '';
      // Cached so CC and BCC share one request per term.
      const users = await queryClient.fetchQuery({
        queryKey: ccBccUserSearchQueryKey(term),
        queryFn: ({ signal }) => fetchUserSearch({ query: term, signal }),
        staleTime: 60_000,
      });
      return register(users.map(toCcBccOption));
    },
    [queryClient, register]
  );

  /** Email for a selected value, from anything the pickers have seen. */
  const getEmailByValue = useCallback((value: string): string => {
    const description = seenRef.current.get(String(value))?.description;
    return typeof description === 'string' ? description : '';
  }, []);

  return { initialOptions, loadOptions, getEmailByValue };
}

/** Puts a kula `Select` into async (searched) mode. */
export const asyncSelectProps = (
  loadOptions: (_query: string) => Promise<SelectOption[]>
) => ({
  variant: 'async' as const,
  // Opens on the first page rather than an empty menu.
  defaultOptions: true,
  loadOptions,
});

const matchesQuery = (option: SelectOption, query: string) => {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  const label = String(option.label ?? '').toLowerCase();
  const description = String(option.description ?? '').toLowerCase();
  return label.includes(term) || description.includes(term);
};

export const SENDER_SEARCH_PAGE_SIZE = 30;

/**
 * From options: the same user search as CC/BCC, plus the surface's own rows.
 *
 * `baseOptions` (senders, shared mailboxes, organizer sentinel) is what the
 * field opens on. Shared mailboxes and the sentinel are matched locally — the
 * user search does not return them.
 */
export const useSenderAutocompleteProps = (baseOptions: SelectOption[]) => {
  const queryClient = useQueryClient();

  const loadOptions = useCallback(
    async (query: string): Promise<SelectOption[]> => {
      const term = query?.trim() ?? '';
      if (!term) return baseOptions;

      // Numeric value = a user; anything else is a shared mailbox or sentinel.
      const isUserOption = (option: SelectOption) =>
        /^\d+$/.test(String(option.value));
      const knownUsersById = new Map(
        baseOptions
          .filter(isUserOption)
          .map((option) => [String(option.value), option])
      );

      const users = await queryClient.fetchQuery({
        queryKey: ['sender-users-search', term] as const,
        queryFn: ({ signal }) =>
          fetchUserSearch({
            query: term,
            items: SENDER_SEARCH_PAGE_SIZE,
            signal,
          }),
        staleTime: 60_000,
      });

      const matchedUsers = users.map(
        (user) => knownUsersById.get(String(user.id)) ?? toCcBccOption(user)
      );

      const matchedNonUsers = baseOptions
        .filter((option) => !isUserOption(option))
        .filter((option) => matchesQuery(option, term));

      return [...matchedUsers, ...matchedNonUsers];
    },
    [baseOptions, queryClient]
  );

  return useMemo(() => asyncSelectProps(loadOptions), [loadOptions]);
};
